// Label reading rules, ported from v1's LabelCollection / SpeciesLabel /
// CustomLabel. An element's labels are an ordered list of
//   { type: 'custom', value, visible }   — free text (hidden ones still name it)
//   { type: 'species', id, keys }        — shows the species' values for `keys`,
//                                          in that order
// Pure — the species cache is passed in.

// Default order of a species label's values (and of its options).
export const SPECIES_KEYS = ['name', 'parent_name', 'scientific_name'];

// One species field as text (meili fields can be arrays — use the first).
export function speciesValue(sp, key) {
  const v = sp?.[key];
  return (Array.isArray(v) ? v[0] : v) || null;
}

export function isVariety(sp) {
  if (!sp) return false;
  return sp.type ? sp.type === 'Variety' : !!sp.parent_name;
}

// The keys a species label can offer: parent name only for varieties (a key
// already switched on stays listed so it can be switched off). Order: `order`
// when given (the list stays put while ticking options on and off — only a
// drag moves them), else the label's own key order first, then the rest.
export function speciesKeyOptions(sp, keys = [], order = null) {
  const offered = SPECIES_KEYS.filter((k) => k !== 'parent_name' || isVariety(sp) || keys.includes(k));
  const base = order ? order.filter((k) => offered.includes(k)) : keys.filter((k) => offered.includes(k));
  return [...base, ...offered.filter((k) => !base.includes(k))];
}

// v1 label.getValue(index): a custom label's text (even hidden — it still
// names the element), a species label's index-th key value.
export function labelValue(label, species = {}, index = 0) {
  if (!label) return null;
  if (label.type === 'custom') return index === 0 ? (label.value || null) : null;
  const key = (label.keys || [])[index];
  return key ? speciesValue(species[label.id], key) : null;
}

// v1 hasLabels(): anything that would show (or name the element).
export function hasLabels(labels = []) {
  return (labels || []).some((l) => (l.type === 'custom' ? !!l.value : (l.keys || []).length > 0));
}

// After v1 LabelCollection.addSpecies: the first plant on an element shows
// its full name set; a later one adds just its common name so the label
// doesn't balloon. Varieties lead with their own name too, then the parent.
export function speciesLabelKeys(plant, labels = []) {
  if (hasLabels(labels)) return ['name'];
  return plant?.type === 'Variety' ? ['name', 'parent_name', 'scientific_name'] : ['name', 'scientific_name'];
}

// v1 LabelCollection.first: the first label with a value, of any type.
export function firstLabel(labels = [], species = {}) {
  for (const l of labels || []) {
    const v = labelValue(l, species);
    if (v) return v;
  }
  return null;
}

// v1 LabelCollection.second: a leading species label's second key, else the
// next label with a value.
export function secondLabel(labels = [], species = {}) {
  const list = labels || [];
  if (list[0]?.type === 'species' && (list[0].keys || []).length > 1) return labelValue(list[0], species, 1);
  for (let i = 1; i < list.length; i++) {
    const v = labelValue(list[i], species);
    if (v) return v;
  }
  return null;
}
